import MuiTextField, { type TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';

export type TextFieldProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
} & Omit<MuiTextFieldProps, 'name' | 'error' | 'defaultValue'>;

/**
 * The one reusable text input for every form (docs/development/FrontendGuidelines.md §8.2) — wraps React Hook
 * Form's `Controller` around MUI `TextField` with consistent error display. A page never wires
 * `Controller` directly to a bare MUI primitive inline.
 */
export function TextField<T extends FieldValues>({
  name,
  control,
  label,
  ...rest
}: TextFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <MuiTextField
          {...field}
          {...rest}
          label={label}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? rest.helperText}
          fullWidth
        />
      )}
    />
  );
}
